import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import * as TimelineBookingsService from "_services/timeline-bookings-service";
import * as TimelineService from "_services/timeline-service";
import * as BookingService from "_services/booking-service";
import { createArrivalDoc } from '_utils/functions';
import { paxCheckOut, setBookingLocation, arrivalPaxCheckIn, deleteTimelineJob, unRejectPax } from './timeline-data';
import { updateBooking } from '../prm-bookings/bookings';
import { selectFlightsByBookingKey } from './timeline-flight-list';
import { resolveBookingBuckets, bookingsForFlight } from './flight-rotation-binding';

const CLIENT_DUPLICATE_SUFFIX = "-duplicate-";

const stripClientDuplicateSuffix = (id) => {
    if (!id || typeof id !== "string") return id;
    const duplicateIndex = id.indexOf(CLIENT_DUPLICATE_SUFFIX);
    return duplicateIndex === -1 ? id : id.slice(0, duplicateIndex);
};

const normalizeClientDuplicateFlightKey = (booking, requestedFlightKey, baseBookingId) => {
    const isBoarding =
        requestedFlightKey?.endsWith("_BOARDING") ||
        booking?.isBoardingBooking ||
        booking?.combinedBooking ||
        booking?.originalBoardingBookingId;

    if (!isBoarding) return requestedFlightKey;

    const boardingKey = stripClientDuplicateSuffix(
        baseBookingId ||
        booking?.originalBoardingBookingId ||
        booking?._id ||
        booking?.id
    );

    return boardingKey?.includes("_BOARDING") ? boardingKey : requestedFlightKey;
};


const updateBookingInState = (state, updatedBooking, { mergeAll = false } = {}) => {

    if (typeof updatedBooking === "string") {
        const bookingId = updatedBooking;
        if (!bookingId) return;

        // iterate all flights because after delete we may not know the original flight key
        for (const key in state.bookingsByFlight) {
            const arr = state.bookingsByFlight[key];
            const idx = arr.findIndex(
                b =>
                    b._id === bookingId ||
                    b.id === bookingId ||
                    b?.originalBookingDoc?._id === bookingId
            );

            if (idx !== -1) {
                arr.splice(idx, 1);

                break;
            }
        }

        return; // stop here for delete
    }

    if (!updatedBooking || !updatedBooking._id) return;

    // Get flight confirmation details to build the key
    const fc = updatedBooking.flightConfirmationDetails || updatedBooking.flightDetails;
    if (!fc) return;

    const key = `${fc.carrierCode}${fc.flightNumber}_${fc.origin}_${fc.destination}_${fc.departureDate}`;

    // Find and update the booking in the list
    if (state.bookingsByFlight[key]) {

        const bookingIndex = state.bookingsByFlight[key].findIndex(
            b => b._id === updatedBooking._id || b.id === updatedBooking._id || b?.originalBookingDoc?._id === updatedBooking._id
        );

        // if booking index is found, update the booking
        if (bookingIndex !== -1) {

            // if mergeAll is true, update the entire booking
            if (mergeAll) {

                // returning iata parameter in the arrival pax check in response
                if (updatedBooking?.iata) {

                    // check type of booking
                    if (updatedBooking?.flightConfirmationDetails?.origin != updatedBooking?.iata) {
                        // arrival booking
                        state.bookingsByFlight[key][bookingIndex] = {
                            ...state.bookingsByFlight[key][bookingIndex],
                            originalBookingDoc: {
                                ...updatedBooking,
                            }
                        };
                    }
                    else {

                        // departure booking
                        state.bookingsByFlight[key][bookingIndex] = {
                            ...state.bookingsByFlight[key][bookingIndex],
                            ...updatedBooking,
                        };
                    }
                }
                else {

                    // Merge all properties from updated booking
                    state.bookingsByFlight[key][bookingIndex] = {
                        ...state.bookingsByFlight[key][bookingIndex],
                        ...updatedBooking,
                    };
                }
            }
            else {
                // Only update paxPositionInfo and paxPositionHistory
                state.bookingsByFlight[key][bookingIndex] = {
                    ...state.bookingsByFlight[key][bookingIndex],
                    paxPositionInfo: updatedBooking.paxPositionInfo,
                    paxPositionHistory: updatedBooking.paxPositionHistory,
                };
            }
        }
    }
};

/**
 * Get timeline bookings
 * Returns bookings data organized by flight
 */
export const getBookings = createAsyncThunk(
    "timelineBookings/getBookings",
    async ({ iata, startDate, endDate, rampBookings = false, airline = '', boardingBookings = false, jobsType = 'all' }) => {
        return TimelineBookingsService.getTimelineBookings({
            iata,
            startDate,
            endDate,
            rampBookings,
            airline,
            boardingBookings,
            jobsType
        });
    }
);

// post bookingsList
export const postBookingList = createAsyncThunk('bookings/postBookingList', async (params) => {
    return TimelineService.postBookings(params);
});

// add flight passenger (dedicated for timeline-v2 flight-information component)
export const addFlightPassenger = createAsyncThunk('bookings/addFlightPassenger', async (params, { rejectWithValue }) => {
    try {
        return await TimelineService.addFlightPassenger(params);
    } catch (e) {
        // the matches don't survive a plain rejection, so pass them as the payload
        if (e.possibleDuplicates) return rejectWithValue({ possibleDuplicates: e.possibleDuplicates });
        throw e;
    }
});

/**
 * Bulk create bookings from an uploaded CSV/XLSX file.
 * Every row reports its own outcome — a failed row never aborts the batch.
 * @param {Object} params - { bookings: [{ rowNumber, name, flightLabel, document }] }
 */
export const bulkCreateBookings = createAsyncThunk('bookings/bulkCreateBookings', async (params, { dispatch }) => {
    return TimelineService.createBulkBookings({
        ...params,
        onProgress: (progress) => dispatch(setBulkUploadProgress(progress)),
    });
});

// duplicate task
export const duplicateTask = createAsyncThunk('timelineBookings/duplicateTask', async (params) => {
    return TimelineBookingsService.duplicateTask(params);
});

/**
 * Update a booking for timeline-v2
 * This is a dedicated thunk for timeline-bookings slice (instead of relying on cross-slice action listening)
 * @param {Object} booking - { id: bookingId, body: { ...fieldsToUpdate } }
 */
export const updateTimelineBooking = createAsyncThunk(
    'timelineBookings/updateTimelineBooking',
    async (booking) => {
        return BookingService.updateBooking(booking);
    }
);

/**
 * Update an ALT pickup task (time, locations, assistance, notes, editHistory)
 * This is for updating task documents (not bookings) - specifically for ALT pickup tasks
 * @param {Object} params - { id, pickUpTime, pickUpLocation, dropOffLocation, passengerInformation, notes, editHistory }
 */
export const updateALTPickupTime = createAsyncThunk(
    'timelineBookings/updateALTPickupTime',
    async (params) => {
        const updatePayload = {
            id: params.id,
        };

        // Only include fields that are provided
        if (params.pickUpTime) {
            updatePayload.pickUpTime = params.pickUpTime;
        }
        if (params.pickUpLocation !== undefined) {
            updatePayload.pickUpLocation = params.pickUpLocation;
        }
        if (params.dropOffLocation !== undefined) {
            updatePayload.dropOffLocation = params.dropOffLocation;
        }
        if (params.passengerInformation !== undefined) {
            updatePayload.passengerInformation = params.passengerInformation;
        }
        if (params.notes !== undefined) {
            updatePayload.notes = params.notes;
        }
        if (params.editHistory !== undefined) {
            updatePayload.editHistory = params.editHistory;
        }

        return TimelineService.updateTask(updatePayload);
    }
);

export const manualUpdateBookingsTimes = createAsyncThunk(
    'timelineBookings/manualUpdateBookingsTimes',
    async (params) => {
        return TimelineService.manualUpdateBookingsTimesService(params);
    }
);

export const deleteBookingNote = createAsyncThunk(
    'timelineBookings/deleteBookingNote',
    async (params) => {
        return TimelineBookingsService.deleteBookingNote(params);
    }
);

const timelineBookingsSlice = createSlice({
    name: 'timelineBookings',
    initialState: {
        bookingsByFlight: {}, // Flight-based dictionary: { "EI101": [booking1, booking2], "BA825": [...] }
        status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'

        // post
        postStatus: 'idle',
        postBookings: {},
        postError: null,

        // add flight passenger (dedicated for timeline-v2 flight-information)
        addFlightPassengerStatus: 'idle',
        addFlightPassengerBookings: [],
        addFlightPassengerError: null,
        addFlightPassengerNeedsDepartureDate: false, // true when 422 is returned
        addFlightPassengerPendingBooking: null, // booking that needs departure date
        addFlightPassengerPossibleDuplicates: [], // [{ booking, matches }] when the server warns
        addFlightPassengerLastRequest: null, // the submission to resend if staff create anyway

        // bulk upload (CSV / XLSX)
        bulkUploadStatus: 'idle',
        bulkUploadResults: [],
        bulkUploadError: null,
        bulkUploadProgress: { completed: 0, total: 0 },

        // task id's
        taskIds: [],
        rampFlightIds: [],
        rampMarshallFlightIds: [],

        // duplicate task
        duplicateStatus: 'idle',
        duplicatedTask: null,
        duplicateError: null,

        // update booking (for timeline-v2)
        updateStatus: 'idle',
        updateError: null,

        // update ALT pickup time
        updateALTPickupStatus: 'idle',
        updateALTPickupError: null,

        error: null,
        queryTime: null, // The last time we fetched data

        // Client-side duplicates — persisted separately so they survive bookingsByFlight refreshes
        clientDuplicates: [], // Array of { booking, flightKey }

        // manual update bookings times
        manualUpdateBookingsTimesStatus: 'idle',
        manualUpdateBookingsTimesError: null,

        // delete booking note
        deleteBookingNoteStatus: 'idle',
        deleteBookingNoteError: null,
    },
    reducers: {
        reset: (state) => {
            state.bookingsByFlight = {};
            state.status = 'idle';
            state.error = null;
            state.queryTime = null;
            state.clientDuplicates = [];
        },
        setQueryTime: (state, action) => {
            state.queryTime = action.payload;
        },
        setTaskIds: (state, action) => {
            state.taskIds = action.payload;
        },
        setRampFlightIds: (state, action) => {
            state.rampFlightIds = action.payload;
        },
        setRampMarshallFlightIds: (state, action) => {
            state.rampMarshallFlightIds = action.payload;
        },
        resetPostBookings: (state) => {
            state.postStatus = 'idle';
            state.postBookings = {};
            state.postError = null;
        },
        resetAddFlightPassenger: (state) => {
            state.addFlightPassengerStatus = 'idle';
            state.addFlightPassengerBookings = [];
            state.addFlightPassengerError = null;
            state.addFlightPassengerNeedsDepartureDate = false;
            state.addFlightPassengerPendingBooking = null;
            state.addFlightPassengerPossibleDuplicates = [];
            state.addFlightPassengerLastRequest = null;
        },
        // staff backed out of the possible-duplicate warning; keep the form as typed
        dismissPossibleDuplicates: (state) => {
            // an arrival that already asked for its departure date goes back to that step
            state.addFlightPassengerStatus = state.addFlightPassengerNeedsDepartureDate ? 'needs_departure_date' : 'idle';
            state.addFlightPassengerPossibleDuplicates = [];
            state.addFlightPassengerLastRequest = null;
        },
        resetBulkUpload: (state) => {
            state.bulkUploadStatus = 'idle';
            state.bulkUploadResults = [];
            state.bulkUploadError = null;
            state.bulkUploadProgress = { completed: 0, total: 0 };
        },
        setBulkUploadProgress: (state, action) => {
            state.bulkUploadProgress = action.payload;
        },
        setAddFlightPassengerNeedsDepartureDate: (state, action) => {
            state.addFlightPassengerNeedsDepartureDate = action.payload.needsDepartureDate;
            state.addFlightPassengerPendingBooking = action.payload.booking || null;
        },
        resetDuplicateTask: (state) => {
            state.duplicateStatus = 'idle';
            state.duplicatedTask = null;
            state.duplicateError = null;
        },
        resetUpdateBooking: (state) => {
            state.updateStatus = 'idle';
            state.updateError = null;
        },
        resetUpdateALTPickup: (state) => {
            state.updateALTPickupStatus = 'idle';
            state.updateALTPickupError = null;
        },
        removeClientDuplicate: (state, action) => {
            // Remove a client-side duplicate by ID (e.g. when it gets assigned or deleted)
            const idToRemove = action.payload;
            state.clientDuplicates = state.clientDuplicates.filter(
                dup => dup.booking._id !== idToRemove
            );
        },
        deleteClientDuplicate: (state, action) => {
            // Fully remove a client-side duplicate from both bookingsByFlight and clientDuplicates
            const idToRemove = action.payload;

            // Remove from clientDuplicates persistence
            state.clientDuplicates = state.clientDuplicates.filter(
                dup => dup.booking._id !== idToRemove
            );

            // Remove from bookingsByFlight
            for (const key in state.bookingsByFlight) {
                const arr = state.bookingsByFlight[key];
                const idx = arr.findIndex(b => b._id === idToRemove);
                if (idx !== -1) {
                    arr.splice(idx, 1);
                    break;
                }
            }
        },
        duplicateBookingLocally: (state, action) => {
            // Client-side duplication for ramp/boarding bookings
            const rawBooking = action.payload?.booking;
            if (!rawBooking) return;
            const flightKey = action.payload?.flightKey;
            const assignedDuplicateIds = action.payload?.assignedDuplicateIds || [];
            if (!flightKey) return;

            // Strip task-specific fields that shouldn't carry over to a new unassigned duplicate
            // These are added during task assignment / worker actions and don't belong on a clean booking
            const {
                // Assignment fields
                employeeId, assignedTime, allocatedTime,
                selectedTimeline, dropOffLocation, pickUpLocation,
                // Task lifecycle flags
                active, completed, acknowledged, stopped,
                cancelled, pending, notPlanned, requireUpdates,
                // Task timing
                startTime, completeTime, acknowledgedTime, checkInTime,
                // Boarding task fields
                boardingStarted, boardingStartTime,
                // Location tracking fields
                isAtLocation, atLocationTime, atLocationInformation,
                atLocationGeofenceVerified, atLocationQrVerified, atLocationBeaconVerified,
                // Passenger position tracking
                paxPositionInfo,
                // Task-specific reformatted fields (keep flightInformation & flightId so duplicates retain flight context)
                passengerInformation,
                taskNotes,
                ...cleanBooking
            } = { ...rawBooking };

            // Use the original booking ID as the base — when duplicating from a duplicate,
            // this ensures IDs stay flat (e.g. abc-duplicate-3) rather than nested (abc-duplicate-1-duplicate-1)
            const baseId = stripClientDuplicateSuffix(action.payload?.baseBookingId || cleanBooking._id || cleanBooking.id);
            if (!baseId) return;
            const normalizedFlightKey = normalizeClientDuplicateFlightKey(cleanBooking, flightKey, baseId);
            const existingBookings = [
                ...(state.bookingsByFlight[normalizedFlightKey] || []),
                ...(normalizedFlightKey === flightKey ? [] : (state.bookingsByFlight[flightKey] || [])),
            ];
            const storedDuplicateIds = state.clientDuplicates
                .map(dup => dup?.booking?._id)
                .filter(id => id?.startsWith(`${baseId}-duplicate-`));

            // Collect ALL known duplicate IDs from both sources (bookingsByFlight + assigned tasks)
            const allDuplicateIds = [
                ...existingBookings
                    .filter(b => b._id?.startsWith(`${baseId}-duplicate-`))
                    .map(b => b._id),
                ...storedDuplicateIds,
                ...assignedDuplicateIds
            ];

            // Find the highest existing duplicate number across all known duplicates
            let maxNumber = 0;
            for (const id of allDuplicateIds) {
                const num = parseInt(id.split("-duplicate-")[1], 10);
                if (!isNaN(num) && num > maxNumber) {
                    maxNumber = num;
                }
            }
            const nextNumber = maxNumber + 1;

            // Get original name (handle different booking structures)
            let originalName = cleanBooking.name || cleanBooking.flightData?.name || '';

            // For ramp/boarding bookings with passengers array, get the first passenger's name
            if (cleanBooking.passengers && cleanBooking.passengers.length > 0) {
                const firstPassenger = cleanBooking.passengers.find(p => p.bookingType !== "DUPLICATE");
                if (firstPassenger) {
                    originalName = firstPassenger.name;
                }
            }

            const duplicateId = `${baseId}-duplicate-${nextNumber}`;

            const duplicatedBooking = {
                ...cleanBooking,
                _id: duplicateId,
                id: duplicateId,
                name: `${originalName} - PART ${nextNumber}`, // Name and ID now use the same number
                isClientDuplicate: true, // Flag to identify client-side duplicates
                clientDuplicateNumber: nextNumber,
                originalBoardingBookingId: baseId,
            };

            console.log('duplicatedBooking ', duplicatedBooking);

            // Add to bookings list
            if (!state.bookingsByFlight[normalizedFlightKey]) {
                state.bookingsByFlight[normalizedFlightKey] = [];
            }

            state.bookingsByFlight[normalizedFlightKey].push(duplicatedBooking);
            // Persist to clientDuplicates so they survive data refreshes
            state.clientDuplicates.push({ booking: duplicatedBooking, flightKey: normalizedFlightKey });
        },
        resetManualUpdateBookingsTimes: (state) => {
            state.manualUpdateBookingsTimesStatus = 'idle';
            state.manualUpdateBookingsTimesError = null;
        },

        resetDeleteBookingNote: (state) => {
            state.deleteBookingNoteStatus = 'idle';
            state.deleteBookingNoteError = null;
        },
    },
    extraReducers: {
        [getBookings.pending]: (state, action) => {
            state.status = "loading";
            state.error = null;
        },
        [getBookings.fulfilled]: (state, action) => {
            state.status = "succeeded";
            state.bookingsByFlight = action.payload || {};
            state.queryTime = new Date(); // Cache query time

            // Re-inject client-side duplicates that were created locally
            // (these only live in Redux and would otherwise be lost on data refresh)
            for (const dup of state.clientDuplicates) {
                const { booking } = dup;
                const flightKey = normalizeClientDuplicateFlightKey(
                    booking,
                    dup.flightKey,
                    booking?.originalBoardingBookingId
                );
                if (!booking || !flightKey) continue;
                dup.flightKey = flightKey;

                if (!state.bookingsByFlight[flightKey]) {
                    state.bookingsByFlight[flightKey] = [];
                }
                // Only re-add if not already present (e.g. if the server now returns this ID)
                const alreadyExists = state.bookingsByFlight[flightKey].some(b => b._id === booking._id);
                if (!alreadyExists) {
                    state.bookingsByFlight[flightKey].push(booking);
                }
            }
        },
        [getBookings.rejected]: (state, action) => {
            state.status = "failed";
            state.bookingsByFlight = {};
            state.error = action.error.message;
        },
        [postBookingList.pending]: (state, action) => {
            state.postStatus = "loading";
            state.postError = null;
        },
        [postBookingList.fulfilled]: (state, action) => {

            state.postStatus = "succeeded";

            // check payload
            const newBookings = Array.isArray(action.payload?.response)
                ? action.payload.response
                : [];
            // for each booking, add to appropriate flight bucket
            for (let booking of newBookings) {

                // get booking details
                const fc = booking.flightConfirmationDetails || booking.flightDetails;

                // adjust id
                const normalizedId = booking._id || booking.id || booking.bookingId;
                booking._id = normalizedId;

                // create flight id
                const key = `${fc.carrierCode}${fc.flightNumber}_${fc.origin}_${fc.destination}_${fc.departureDate}`;

                // ensure flight key exists
                if (!state.bookingsByFlight[key]) {
                    state.bookingsByFlight[key] = [];
                }

                // prevent duplicates by _id
                const exists = state.bookingsByFlight[key].some(b => b._id === booking._id);

                if (exists) {
                    // skip duplicates
                }
                else {
                    // check if arrival format the booking to be the timeline arrival format
                    if (booking?.iata === booking?.flightConfirmationDetails?.destination) {
                        booking = createArrivalDoc(booking, booking?.iata, state.bookingsByFlight[key]);
                    }
                    state.bookingsByFlight[key].push(booking);
                }
            }
        },
        [postBookingList.rejected]: (state, action) => {
            state.postStatus = "failed";
            state.postBookings = {};
            state.postError = action.error.message;
        },
        // addFlightPassenger reducers (dedicated for timeline-v2 flight-information)
        [addFlightPassenger.pending]: (state, action) => {
            state.addFlightPassengerStatus = "loading";
            state.addFlightPassengerError = null;
        },
        [addFlightPassenger.fulfilled]: (state, action) => {

            state.addFlightPassengerStatus = "succeeded";

            // check payload
            const newBookings = Array.isArray(action.payload?.response)
                ? action.payload.response
                : [];
            // for each booking, add to appropriate flight bucket
            for (let booking of newBookings) {

                // get booking details
                const fc = booking.flightConfirmationDetails || booking.flightDetails;

                // adjust id
                const normalizedId = booking._id || booking.id || booking.bookingId;
                booking._id = normalizedId;

                // create flight id
                const key = `${fc.carrierCode}${fc.flightNumber}_${fc.origin}_${fc.destination}_${fc.departureDate}`;

                // ensure flight key exists
                if (!state.bookingsByFlight[key]) {
                    state.bookingsByFlight[key] = [];
                }

                // prevent duplicates by _id
                const exists = state.bookingsByFlight[key].some(b => b._id === booking._id);

                if (exists) {
                    // skip duplicates
                }
                else {
                    // check if arrival format the booking to be the timeline arrival format
                    if (booking?.iata === booking?.flightConfirmationDetails?.destination) {
                        booking = createArrivalDoc(booking, booking?.iata, state.bookingsByFlight[key]);
                    }
                    state.bookingsByFlight[key].push(booking);
                }
            }
            state.addFlightPassengerBookings = newBookings;
        },
        [addFlightPassenger.rejected]: (state, action) => {
            if (action.payload?.possibleDuplicates) {
                state.addFlightPassengerStatus = "possible_duplicate";
                state.addFlightPassengerPossibleDuplicates = action.payload.possibleDuplicates;
                state.addFlightPassengerLastRequest = action.meta.arg;
                state.addFlightPassengerError = null;
            }
            // Check if it's a 422 error (departure date required)
            else if (action.error.message === "DEPARTURE_DATE_REQUIRED") {
                state.addFlightPassengerStatus = "needs_departure_date";
                state.addFlightPassengerNeedsDepartureDate = true;
                state.addFlightPassengerError = null;
            } else {
                state.addFlightPassengerStatus = "failed";
                state.addFlightPassengerBookings = [];
                state.addFlightPassengerError = action.error.message;
            }
        },
        [bulkCreateBookings.pending]: (state) => {
            state.bulkUploadStatus = 'loading';
            state.bulkUploadError = null;
            state.bulkUploadResults = [];
        },
        [bulkCreateBookings.fulfilled]: (state, action) => {
            state.bulkUploadStatus = 'succeeded';
            state.bulkUploadResults = action.payload?.results || [];
        },
        [bulkCreateBookings.rejected]: (state, action) => {
            state.bulkUploadStatus = 'failed';
            state.bulkUploadResults = [];
            state.bulkUploadError = action.error.message;
        },
        [duplicateTask.pending]: (state) => {
            state.duplicateStatus = "loading";
            state.duplicateError = null;
        },
        [duplicateTask.fulfilled]: (state, action) => {
            state.duplicateStatus = "succeeded";
            state.duplicatedTask = action.payload;

            // Extract the duplicate booking from the response
            const duplicateBooking = action.payload.booking;
            const originalBooking = action.payload.originalBooking;

            if (duplicateBooking) {
                const fc = duplicateBooking.flightConfirmationDetails || duplicateBooking.flightDetails;

                if (fc) {
                    const key = `${fc.carrierCode}${fc.flightNumber}_${fc.origin}_${fc.destination}_${fc.departureDate}`;

                    // Ensure flight key exists
                    if (!state.bookingsByFlight[key]) {
                        state.bookingsByFlight[key] = [];
                    }

                    // Append part number to the name if it's a duplicate
                    const bookingWithUpdatedName = {
                        ...duplicateBooking,
                        name: duplicateBooking.partNumber
                            ? `${duplicateBooking.name} - PART ${duplicateBooking.partNumber}`
                            : duplicateBooking.name
                    };

                    // Check if arrival - format the booking to be the timeline arrival format.
                    // Use the portal's airport: SITA/Connect bookings carry no iata, and leaving them
                    // unformatted creates a task with no ARR_ prefix or originalBookingId.
                    const iata = action.meta.arg?.iata || bookingWithUpdatedName?.iata;
                    let formattedBooking = bookingWithUpdatedName;
                    if (iata && iata === fc.destination) {
                        formattedBooking = {
                            ...createArrivalDoc(bookingWithUpdatedName, iata, state.bookingsByFlight[key]),
                            // match prm-bookings' arrival job id so the task still pairs with this
                            // job after the timeline refetches
                            _id: `ARR_${duplicateBooking._id}`,
                        };
                    }

                    // Add duplicate to bookings list
                    state.bookingsByFlight[key].push(formattedBooking);

                    // Update the original booking in the list with the updated duplicateBookings array
                    if (originalBooking) {
                        const originalIndex = state.bookingsByFlight[key].findIndex(
                            b => b._id === originalBooking._id || b._id === originalBooking.id
                        );

                        if (originalIndex !== -1) {
                            // Update the original booking with the new duplicateBookings array
                            const updatedOriginal = { ...state.bookingsByFlight[key][originalIndex] };

                            // If it's an arrival doc, update the nested originalBookingDoc
                            if (updatedOriginal.originalBookingDoc) {
                                updatedOriginal.originalBookingDoc = {
                                    ...updatedOriginal.originalBookingDoc,
                                    duplicateBookings: originalBooking.duplicateBookings
                                };
                            } else {
                                updatedOriginal.duplicateBookings = originalBooking.duplicateBookings;
                            }

                            state.bookingsByFlight[key][originalIndex] = updatedOriginal;
                        }
                    }
                }
            }
        },
        [duplicateTask.rejected]: (state, action) => {
            state.duplicateStatus = "failed";
            state.duplicatedTask = null;
            state.duplicateError = action.error.message;
        },

        // Update booking position info when setBookingLocation succeeds (checked-in, etc.)
        [setBookingLocation.fulfilled]: (state, action) => {
            updateBookingInState(state, action.payload, {});
        },

        // Update booking position info when paxCheckOut succeeds
        [paxCheckOut.fulfilled]: (state, action) => {
            updateBookingInState(state, action.payload, {});
        },

        // Update entire booking when updateBooking succeeds
        [updateBooking.fulfilled]: (state, action) => {
            updateBookingInState(state, action.payload, { mergeAll: true });
        },

        [arrivalPaxCheckIn.fulfilled]: (state, action) => {
            updateBookingInState(state, action?.payload?.booking ?? null, { mergeAll: true });
        },
        [unRejectPax.fulfilled]: (state, action) => {
            updateBookingInState(state, action?.payload?.bookingDoc, { mergeAll: true });
        },

        // updateTimelineBooking - dedicated thunk for timeline-v2
        [updateTimelineBooking.pending]: (state) => {
            state.updateStatus = 'loading';
            state.updateError = null;
        },
        [updateTimelineBooking.fulfilled]: (state, action) => {
            state.updateStatus = 'succeeded';
            updateBookingInState(state, action.payload, { mergeAll: true });
        },
        [updateTimelineBooking.rejected]: (state, action) => {
            state.updateStatus = 'failed';
            state.updateError = action.error.message;
        },
        [deleteTimelineJob.fulfilled]: (state, action) => {
            updateBookingInState(state, action.payload.jobId, {});
        },


        // updateALTPickupTime - for updating pickup time on ALT pickup tasks
        [updateALTPickupTime.pending]: (state) => {
            state.updateALTPickupStatus = 'loading';
            state.updateALTPickupError = null;
        },
        [updateALTPickupTime.fulfilled]: (state, action) => {
            state.updateALTPickupStatus = 'succeeded';
            // Update the task in bookingsByFlight if it exists there
            // ALT pickup tasks have flightInformation instead of flightConfirmationDetails
            const updatedTask = action.payload;
        },
        [updateALTPickupTime.rejected]: (state, action) => {
            state.updateALTPickupStatus = 'failed';
            state.updateALTPickupError = action.error.message;
        },

        // manual update bookings times
        [manualUpdateBookingsTimes.pending]: (state) => {
            state.manualUpdateBookingsTimesStatus = 'loading';
            state.manualUpdateBookingsTimesError = null;
        },
        [manualUpdateBookingsTimes.fulfilled]: (state, action) => {
            state.manualUpdateBookingsTimesStatus = 'succeeded';
        },
        [manualUpdateBookingsTimes.rejected]: (state, action) => {
            state.manualUpdateBookingsTimesStatus = 'failed';
            state.manualUpdateBookingsTimesError = action.error.message;
        },

        // delete booking note
        [deleteBookingNote.pending]: (state) => {
            state.deleteBookingNoteStatus = 'loading';
            state.deleteBookingNoteError = null;
        },
        [deleteBookingNote.fulfilled]: (state, action) => {
            state.deleteBookingNoteStatus = 'succeeded';
        },
        [deleteBookingNote.rejected]: (state, action) => {
            state.deleteBookingNoteStatus = 'failed';
            state.deleteBookingNoteError = action.error.message;
        },
    }
});

export default timelineBookingsSlice.reducer;

// Selectors
export const selectStatus = state => state.timelineBookings.status;
export const selectError = state => state.timelineBookings.error;
export const selectQueryTime = state => state.timelineBookings.queryTime;

// post selectors
export const selectPostedBookings = state => state.timelineBookings.postBookings;
export const selectPostedBookingsStatus = state => state.timelineBookings.postStatus;
export const selectPostedBookingsError = state => state.timelineBookings.postError;

// addFlightPassenger selectors (dedicated for timeline-v2 flight-information)
export const selectAddFlightPassengerBookings = state => state.timelineBookings.addFlightPassengerBookings;
export const selectAddFlightPassengerStatus = state => state.timelineBookings.addFlightPassengerStatus;
export const selectAddFlightPassengerError = state => state.timelineBookings.addFlightPassengerError;
export const selectAddFlightPassengerNeedsDepartureDate = state => state.timelineBookings.addFlightPassengerNeedsDepartureDate;
export const selectAddFlightPassengerPendingBooking = state => state.timelineBookings.addFlightPassengerPendingBooking;
export const selectAddFlightPassengerPossibleDuplicates = state => state.timelineBookings.addFlightPassengerPossibleDuplicates;
export const selectAddFlightPassengerLastRequest = state => state.timelineBookings.addFlightPassengerLastRequest;

// bulk upload selectors
export const selectBulkUploadStatus = state => state.timelineBookings.bulkUploadStatus;
export const selectBulkUploadResults = state => state.timelineBookings.bulkUploadResults;
export const selectBulkUploadError = state => state.timelineBookings.bulkUploadError;
export const selectBulkUploadProgress = state => state.timelineBookings.bulkUploadProgress;

/**
 * OPTIMIZED: Select bookings for a specific flight
 * This selector only causes re-renders when the specific flight's bookings change
 * 
 * @param {string} flightNumber - Flight number (e.g., "EI101", "BA825")
 * @returns {Function} Selector function that returns bookings for the specified flight
 * 
 * @example
 * const bookingsForEI101 = useSelector(selectBookingsByFlight("EI101"));
 */
//export const selectBookingsByFlight = (flightNumber) => (state) => {
//  return state.timelineBookings.bookingsByFlight[flightNumber] || [];
//};
/**
 * Every booking bound to the flight row it is actually on, keyed by that row's bookingKey.
 *
 * The pairing is by time, not by date string — see flight-rotation-binding.js for why the
 * dates on the two sides cannot be trusted to agree for overnight arrivals or for two
 * rotations of one flight number in the same window.
 */
export const selectResolvedBookingsByFlight = createSelector(
    [
        (state) => state.timelineBookings.bookingsByFlight,
        selectFlightsByBookingKey,
    ],
    resolveBookingBuckets
);

/**
 * Timeline booking docs keyed by _id, e.g. to find an ALT pickup's original booking (originalALTPaxId)
 */
export const selectTimelineBookingsById = createSelector(
    (state) => state.timelineBookings.bookingsByFlight,
    (bookingsByFlight) => {
        const byId = {};
        Object.values(bookingsByFlight || {}).forEach((bookings) => {
            if (!Array.isArray(bookings)) return;
            bookings.forEach((booking) => {
                if (booking && booking._id) byId[booking._id] = booking;
            });
        });
        return byId;
    }
);

/**
 * Bookings on a flight row, unfiltered — used by the flight menus, the pax list and the
 * manual time update, which need the full population of the row.
 */
export const selectAllBookingsByFlight = (flightNumber) =>
    createSelector(
        selectResolvedBookingsByFlight,
        (resolved) => bookingsForFlight(resolved, flightNumber)
    );

/* NEW BOOKINGS BY FLIGHT WITH FILTERING */
export const selectBookingsByFlight = (flightNumber) =>
    createSelector(
        [
            selectResolvedBookingsByFlight,
            state => state.timelineBookings.taskIds,   // stored as array or Set
        ],
        (resolved, taskIds) => {
            const bookings = bookingsForFlight(resolved, flightNumber);

            if (!taskIds || taskIds.length === 0) return bookings;

            const blocked = new Set(taskIds);

            return bookings.filter(b => {

                if (b?._id?.startsWith("CONNECT-") || b?.isConnectBooking) {
                    return false;
                }

                // Check if this specific booking ID is blocked
                if (blocked.has(b?._id)) {
                    return false;
                }

                // For duplicate bookings (backend or client-side), only filter by exact ID
                // Client duplicates use originalBoardingBookingId (not originalBookingId) so they won't be caught below
                if (b?.bookingType === "DUPLICATE" || b?.isClientDuplicate === true) {
                    return true;
                }

                // For non-duplicate bookings, also check originalBookingId
                // This handles arrival bookings where originalBookingId links back to the departure doc
                if (blocked.has(b?.originalBookingId)) {
                    return false;
                }

                return true;
            });

        }
    );

/**
 * Row keys (bookingKey) of flights whose PRM work is all finished, for the "Hide Completed Flights" switch.
 *
 * A row is complete when it has at least one task, every task on it is completed or cancelled, and every
 * pax card still showing under it is already boarded / completed. Rows with no tasks, a stopped or open
 * task, or an unassigned pax (e.g. a landed arrival nobody was sent to) stay visible.
 *
 * Tasks are matched to the row by flightId and by kind, since a departure, its boarding card and its ramp
 * card all share one flightId.
 */
const isTaskFinished = (task) => task.completed || task.cancelled;
const isCardFinished = (booking) => booking.selectBoardingPaxStatus === "boarded" || booking.paxPositionInfo?.activity === "Completed";

const getTaskRowKind = (task) => {
    if (task.combinedRampBooking) return "ramp";
    if (task.isBoardingBooking || task.combinedBooking || task.combinedMarshallBooking) return "boarding";
    return "flight";
};

const getFlightRowKind = (flight) => {
    if (flight.type === "ramp" || flight.isRampTask) return "ramp";
    if (flight.type === "boarding" || flight.isBoardingFlight) return "boarding";
    return "flight";
};

export const selectCompletedFlightKeys = createSelector(
    [
        (state) => state.timelineFlightList.flightsByTime,
        selectResolvedBookingsByFlight,
        (state) => state.timelineBookings.taskIds,
        (state) => state.timelineData.taskList,
    ],
    (flightsByTime, resolved, taskIds, taskList) => {
        const completed = new Set();
        if (!flightsByTime || !Array.isFullArray(taskList)) return completed;

        // tasks grouped by flightId + row kind; gate relocations hold every row of their flight open
        const tasksByRow = {};
        const openGateRelocations = new Set();
        for (const task of taskList) {
            if (!task.flightId) continue;
            if (task.isGateRelocation) {
                if (!isTaskFinished(task)) openGateRelocations.add(task.flightId);
                continue;
            }
            const rowId = `${task.flightId}|${getTaskRowKind(task)}`;
            (tasksByRow[rowId] = tasksByRow[rowId] || []).push(task);
        }

        const blocked = new Set(taskIds);

        for (const time in flightsByTime) {
            for (const flight of flightsByTime[time] || []) {
                const rowKey = flight.bookingKey || flight.flightNumber || flight._id;
                if (openGateRelocations.has(flight.flightId)) continue;

                const tasks = tasksByRow[`${flight.flightId}|${getFlightRowKind(flight)}`];
                if (!Array.isFullArray(tasks) || !tasks.every(isTaskFinished)) continue;

                // same visibility rule as selectBookingsByFlight: cards not covered by a task
                const openCards = bookingsForFlight(resolved, rowKey).filter((b) => {
                    if (b._id?.startsWith("CONNECT-") || b.isConnectBooking) return false;
                    if (blocked.has(b._id)) return false;
                    if (b.bookingType !== "DUPLICATE" && !b.isClientDuplicate && blocked.has(b.originalBookingId)) return false;
                    return !isCardFinished(b);
                });
                if (openCards.length > 0) continue;

                completed.add(rowKey);
            }
        }

        return completed;
    }
);

/* OLD SELECT BOOKINGS BY FLIGHT WITHOUT FILTERING!
export const selectBookingsByFlight = (flightNumber) => (state) => {
  return state.timelineBookings.bookingsByFlight[flightNumber] || [];
};
*/


/**
 * Select all bookings grouped by flight
 */
export const selectBookingsByFlightDict = state => {
    return state.timelineBookings.bookingsByFlight;
};

/**
 * Select all flight numbers that have bookings
 */
export const selectFlightsWithBookings = state => {
    return Object.keys(state.timelineBookings.bookingsByFlight).sort();
};

/**
 * Select all bookings as a flat array
 */
export const selectBookingsArray = state => {
    return Object.values(state.timelineBookings.bookingsByFlight).flat();
};

/**
 * Select total booking count
 */
export const selectTotalBookingCount = state => {
    return Object.values(state.timelineBookings.bookingsByFlight).reduce(
        (total, bookings) => total + (bookings?.length || 0),
        0
    );
};

// task ids
export const selectTaskIds = state => {
    return state.timelineBookings.taskIds;
}

// check if task id exists
export const selectHasTaskId = (taskId) => (state) => {
    const taskIds = state.timelineBookings.taskIds;
    if (!taskIds || taskIds.length === 0) return false;
    return taskIds.includes(taskId);
};
// check if task id exists
export const selectHasRampFlightId = (taskId) => (state) => {
    const taskIds = state.timelineBookings.taskIds;
    if (!taskIds || taskIds.length === 0) return false;
    return taskIds.includes(taskId);
};
// duplicate task selectors
export const selectDuplicateStatus = state => state.timelineBookings.duplicateStatus;
export const selectDuplicatedTask = state => state.timelineBookings.duplicatedTask;
export const selectDuplicateError = state => state.timelineBookings.duplicateError;

// update booking selectors (for timeline-v2)
export const selectUpdateStatus = state => state.timelineBookings.updateStatus;
export const selectUpdateError = state => state.timelineBookings.updateError;

// update ALT pickup selectors
export const selectUpdateALTPickupStatus = state => state.timelineBookings.updateALTPickupStatus;
export const selectUpdateALTPickupError = state => state.timelineBookings.updateALTPickupError;

// manual update bookings times selectors
export const selectManualUpdateBookingsTimesStatus = state => state.timelineBookings.manualUpdateBookingsTimesStatus;
export const selectManualUpdateBookingsTimesError = state => state.timelineBookings.manualUpdateBookingsTimesError;

// ramp flight ids selectors
export const selectRampFlightIds = state => state.timelineBookings.rampFlightIds;
export const selectRampMarshallFlightIds = state => state.timelineBookings.rampMarshallFlightIds;

// delete booking note selectors
export const selectDeleteBookingNoteStatus = state => state.timelineBookings.deleteBookingNoteStatus;
export const selectDeleteBookingNoteError = state => state.timelineBookings.deleteBookingNoteError;
// Export actions
export const { reset, setQueryTime, setTaskIds, resetPostBookings, resetAddFlightPassenger, dismissPossibleDuplicates, resetBulkUpload, setBulkUploadProgress, setAddFlightPassengerNeedsDepartureDate, resetDuplicateTask, resetUpdateBooking, resetUpdateALTPickup, duplicateBookingLocally, removeClientDuplicate, deleteClientDuplicate, resetManualUpdateBookingsTimes, setRampFlightIds, setRampMarshallFlightIds, resetDeleteBookingNote } = timelineBookingsSlice.actions;
